import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';

/**
 * A page that says what happened instead of quietly going somewhere else.
 *
 * Every unmatched route used to redirect to the dashboard. That turned a
 * broken link into a mystery: an officer clicking "Open the onboarding
 * record" landed on the dashboard with no message, and there was nothing to
 * report except "the button does not work" — which is how DEV-22 was closed
 * once and reopened.
 *
 * The address is shown because it is the only evidence of what was actually
 * asked for, and it is what makes the next report actionable.
 */
export function NotFoundPage(): JSX.Element {
  const { pathname } = useLocation();

  return (
    <Box sx={{ p: { xs: 2.5, sm: 4 }, maxWidth: 720 }}>
      <Stack spacing={2}>
        <Typography variant="h5">That page does not exist</Typography>
        <Alert severity="warning">
          Nothing is registered at <strong>{pathname}</strong>. You have not been signed out and
          nothing has changed — the link simply points somewhere that is not there.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          If you reached this from a button inside the console, it is a defect worth reporting with
          the address above: a link built from a missing identifier lands here rather than on the
          record it names.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" component={RouterLink} to="/dashboard">
            Go to the dashboard
          </Button>
          <Button component={RouterLink} to="/customers">
            Find a customer
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
